import * as React from "react"
import {useTranslation} from "gatsby-plugin-react-i18next"

import {useConfirmModal} from "stores/modals"
import {Button} from "../buttons"
import {CircledIcon} from "../icons"
import {Dialog} from "@headlessui/react"
import V3AlertDialog from "./V3AlertDialog"

/**
 * @typedef {object} ConfirmModalProps
 */

/**
 * @type {React.FC<ConfirmModalProps>}
 */
const ConfirmModal = () => {
  const {closeModal, showModal, showWarning, message, handler} =
    useConfirmModal()
  const {t} = useTranslation()

  const handleConfirm = async () => {
    await handler?.handleConfirm?.()
    closeModal()
  }

  const handleCancel = async () => {
    await handler?.handleCancel?.()
    closeModal()
  }

  if (!handler.showCancel && handler.showConfirm !== false) {
    return (
      <V3AlertDialog
        open={showModal}
        message={message}
        confirmText={handler.confirmText || t("common.ok")}
        onConfirm={handleConfirm}
      />
    )
  }

  return (
    <Dialog open={showModal} onClose={() => {}} className="relative z-50">
      <Dialog.Backdrop
        className="fixed top-0 right-0 bottom-0 left-0 z-40 bg-black bg-opacity-50 lg:bg-dark lg:bg-opacity-20 dark:bg-opacity-80"
        aria-hidden={true}
      />
      <div className="fixed top-0 right-0 bottom-0 left-0 flex items-center justify-center px-4">
        <Dialog.Panel
          className={`
            grid w-full max-w-[280px] grid-cols-1 place-items-center gap-5
            rounded-2xl bg-white px-6 py-8 text-neutral-black shadow-lg
            lg:w-[600px] lg:max-w-none lg:gap-10 lg:rounded-3xl lg:p-4 lg:py-16 lg:text-dark
            dark:bg-midnight dark:text-white
          `}
        >
          {showWarning ? (
            <CircledIcon.Warning className="h-16 w-16 lg:h-24 lg:w-24" />
          ) : null}

          <p className="typo-v3-content-bold-1 text-center lg:typo-body-1">
            {message}
          </p>
          <div className="flex w-full flex-row justify-center">
            {handler.showCancel ? (
              <Button
                className="mr-4 flex-1 peer-last:mr-0 lg:mr-10 lg:flex-none"
                onClick={handleCancel}
                hidden={!handler.showCancel}
                {...handler.cancelButtonProps}
              >
                {handler.cancelText || t("common.cancel")}
              </Button>
            ) : null}
            {handler.showConfirm ? (
              <Button
                className="flex-1 lg:flex-none"
                onClick={handleConfirm}
                hidden={!handler.showConfirm}
                {...handler.confirmButtonProps}
              >
                {handler.confirmText || t("common.ok")}
              </Button>
            ) : null}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default ConfirmModal
